/**
 * useDailyReport — İki tetikleyici:
 *   1. 07:00 — Sabah Aksiyon Bülteni (bugün yapılacaklar, uyarılar, cihaz durumu)
 *   2. 23:55 — Gün Sonu Özet Raporu (istatistikler, alert sayısı)
 *
 * localStorage'a tarih damgası yazar; aynı gün iki kez gönderilmez.
 */

import { useEffect, useRef } from 'react';
import {
  sendDailyReport,
  sendTelegramDailyReport,
  sendTelegramMorningBulletin,
  type MorningBulletinData,
} from '../services/notificationService';
import type { GrowBag, AlertEvent, SensorReading, AlertNotificationSettings, DeviceStatus } from '../types';

const LS_KEY_EVENING = 'gmc_daily_report_sent';
const LS_KEY_MORNING = 'gmc_morning_bulletin_sent';

const EVENING_HOUR   = 23;
const EVENING_MINUTE = 55;
const MORNING_HOUR   = 7;
const MORNING_MINUTE = 0;
const CHECK_INTERVAL_MS = 60_000; // her dakika kontrol

// ── Hasat penceresi hesabı ────────────────────────────────────────────────────
// Lion's Mane: fruiting başlangıcından 5-10 gün
// Shiitake: 7-14 gün
// Oyster: 5-7 gün
const HARVEST_WINDOW: Record<string, { min: number; max: number }> = {
  'lions-mane': { min: 5, max: 10 },
  'shiitake':   { min: 7, max: 14 },
  'oyster':     { min: 5, max: 7  },
};

function daysSince(dateStr: string): number {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / 86_400_000);
}

function buildMorningBulletin(
  bags: GrowBag[],
  alerts: AlertEvent[],
  devices: DeviceStatus[],
  healthScore: number,
): MorningBulletinData {
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);

  const todayActions: MorningBulletinData['todayActions'] = [];
  const warnings: MorningBulletinData['warnings'] = [];

  for (const bag of bags) {
    if (bag.status === 'retired') continue;

    // İncubasyon → meyvelemeye al
    if (bag.status === 'incubation' && bag.inoculationDate) {
      const days = daysSince(bag.inoculationDate);
      const target = 21; // ortalama incubasyon süresi
      if (days >= target) {
        todayActions.push({
          bagId: bag.id,
          bagCode: bag.name,
          action: 'Meyvelemeye al',
          reason: `${days}. gün, hedef ${target} gün`,
          priority: days > target + 5 ? 'high' : 'medium',
        });
      }
    }

    // Meyveleme → hasat penceresi
    if (bag.status === 'fruiting' && bag.fruitingStartDate) {
      const days = daysSince(bag.fruitingStartDate);
      const speciesId = bag.speciesId ?? 'lions-mane';
      const harvestWin = HARVEST_WINDOW[speciesId] ?? { min: 5, max: 10 };
      if (days >= harvestWin.min && days <= harvestWin.max) {
        todayActions.push({
          bagId: bag.id,
          bagCode: bag.name,
          action: 'Hasat et',
          reason: `${days}. gün, hasat penceresi ${harvestWin.min}-${harvestWin.max} gün`,
          priority: days >= harvestWin.max ? 'high' : 'medium',
        });
      }
    }
  }

  // Dünün uyarıları
  const yesterdayAlerts = alerts.filter(a => a.timestamp.startsWith(yesterday));
  if (yesterdayAlerts.length > 0) {
    const critCount = yesterdayAlerts.filter(a => a.severity === 'critical').length;
    if (critCount > 0) {
      warnings.push({ message: `Dün ${critCount} kritik alarm tetiklendi` });
    }
  }

  // Cihaz hataları
  const offlineDevices = devices.filter(d => d.haEntityId && !d.isActive);
  for (const dev of offlineDevices) {
    warnings.push({ message: `${dev.name} kapalı görünüyor` });
  }

  // Dünün hasatı — flushHarvests içinde dünün tarihine uyan kayıtlar
  const yesterdayHarvestGrams = bags.reduce((sum, bag) => {
    const flushes = (bag.flushHarvests ?? []).filter((fh: { harvestDate?: string; yieldGrams?: number }) =>
      fh.harvestDate?.startsWith(yesterday)
    );
    return sum + flushes.reduce((s: number, fh: { yieldGrams?: number }) => s + (fh.yieldGrams ?? 0), 0);
  }, 0);

  const activeBagCount = bags.filter(b => b.status !== 'retired').length;
  // retiredReason alanı yok — sadece retired sayısını kullan
  const lostBagCount = bags.filter(b => b.status === 'retired').length;

  const now = new Date();
  const dateStr = now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

  return {
    date: dateStr,
    todayActions,
    warnings,
    yesterdayHarvestGrams,
    activeBagCount,
    lostBagCount,
    healthScore,
    devices: devices.map(d => ({ name: d.name, isActive: d.isActive })),
  };
}

interface UseDailyReportOpts {
  bags: GrowBag[];
  alerts: AlertEvent[];
  todayReadings: SensorReading[];
  healthScore: number;
  healthLabel: string;
  notifSettings: AlertNotificationSettings;
  emailTo: string;
  devices?: DeviceStatus[];
}

export function useDailyReport({
  bags,
  alerts,
  todayReadings,
  healthScore,
  healthLabel,
  notifSettings,
  emailTo,
  devices = [],
}: UseDailyReportOpts): void {
  // Ref keeps latest values without recreating the interval on every data change
  const latestRef = useRef({ bags, alerts, todayReadings, healthScore, healthLabel, notifSettings, emailTo, devices });
  useEffect(() => {
    latestRef.current = { bags, alerts, todayReadings, healthScore, healthLabel, notifSettings, emailTo, devices };
  });

  useEffect(() => {
    const check = async () => {
      const { bags, alerts, todayReadings, healthScore, healthLabel, notifSettings, emailTo, devices } = latestRef.current;
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const today = now.toISOString().slice(0, 10);

      // ── 07:00 Sabah Bülteni ──────────────────────────────────────────────
      if (h === MORNING_HOUR && m === MORNING_MINUTE) {
        if (
          notifSettings.telegram?.enabled &&
          localStorage.getItem(LS_KEY_MORNING) !== today
        ) {
          const bulletin = buildMorningBulletin(bags, alerts, devices, healthScore);
          localStorage.setItem(LS_KEY_MORNING, today);
          const ok = await sendTelegramMorningBulletin(bulletin);
          if (!ok) localStorage.removeItem(LS_KEY_MORNING);
        }
      }

      // ── 23:55 Gün Sonu Raporu ────────────────────────────────────────────
      if (h === EVENING_HOUR && m === EVENING_MINUTE) {
        if (!notifSettings.alertTypes.dailyReport) return;
        if (localStorage.getItem(LS_KEY_EVENING) === today) return;

        const activeBags = bags.filter(b => b.status === 'fruiting' || b.status === 'incubation');
        const todayAlerts = alerts.filter(a => a.timestamp.startsWith(today));
        const criticalCount = todayAlerts.filter(a => a.severity === 'critical').length;

        const avgTemp = todayReadings.length
          ? parseFloat((todayReadings.reduce((s, r) => s + r.temperature, 0) / todayReadings.length).toFixed(1))
          : 0;
        const avgHumidity = todayReadings.length
          ? parseFloat((todayReadings.reduce((s, r) => s + r.humidity, 0) / todayReadings.length).toFixed(1))
          : 0;
        const avgCo2 = todayReadings.length
          ? Math.round(todayReadings.reduce((s, r) => s + r.co2, 0) / todayReadings.length)
          : 0;

        const data = {
          date: now.toLocaleDateString('tr-TR'),
          activeBagCount: activeBags.length,
          healthScore,
          healthLabel,
          avgTemp,
          avgHumidity,
          avgCo2,
          alertCount: todayAlerts.length,
          criticalAlertCount: criticalCount,
        };

        localStorage.setItem(LS_KEY_EVENING, today);
        const results = await Promise.allSettled([
          emailTo ? sendDailyReport(emailTo, data) : Promise.resolve(false),
          notifSettings.telegram?.enabled ? sendTelegramDailyReport(data) : Promise.resolve(false),
        ]);
        const anySucceeded = results.some(r => r.status === 'fulfilled' && r.value === true);
        const anyAttempted = emailTo || notifSettings.telegram?.enabled;
        if (anyAttempted && !anySucceeded) {
          localStorage.removeItem(LS_KEY_EVENING);
        }
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []); // Stable interval — latest values read from ref
}
